// HTTP 라우트 계약
//
// 라우트를 서버 코드가 아니라 이 표에 적는 이유는 둘이다. 하나는 계약 스냅샷이
// 이 표를 그대로 찍어 커밋 전에 diff 를 보여 주기 위해서이고, 다른 하나는 "이 표에
// 없는 표면은 존재하지 않는다" 를 기계가 검사할 수 있게 하기 위해서다.
//
// 인증 등급은 셋이다 — `public`(누구나) · `session`(입장한 사람, CT 도 포함) ·
// `ct`(관리 코드로 증명한 사람만). **`ct` 등급이 CT 권한의 유일한 방어선이다.** 공용
// 입장 코드로 얻은 세션 토큰은 이 등급을 통과하지 못한다.
//
// ── 2026-08-10 개정: 접속 기반 스텝이 쓸 표면 일곱을 세웠다 ────────────────────
//
// 이 파일은 발사대 스텝이 소유하고 다른 스텝은 읽기만 하는 것이 규약이라, 개정은
// **접속 기반 스텝을 시작하기 전에 별도 커밋으로** 냈다. 그 스텝의 커밋에는 계약
// 변경이 들어 있지 않다.
//
// 왜 필요했는가. 발사대가 세워 둔 여덟 중 CT 가 쓰는 쓰기 표면이 `ctClaim` 하나뿐이라,
// 판정 계약이 접속 스텝에 요구하는 케이스 넷을 담을 자리가 없었다 — CT 닉네임 세우기,
// 코드 패널의 드러냄과 재발급, whitelist 등록, CT 추방이다. 여기에 하나 더, 「코드가
// 맞으면 닉네임이 표시되고 그다음에 게임 ID 를 넣는다」는 순서를 지키려면 코드만
// 검증하는 표면이 따로 있어야 했다.
//
// **더한 일곱은 전부 케이스 하나씩에 묶여 있다.** 앞을 내다보고 미리 세운 표면은 없다 —
// 계약에 있는 것은 곧 만들어야 하는 것이고, 안 만들면 스냅샷이 거짓말을 시작한다.
//
// **room 을 만드는 라우트는 여기 없다. 없는 것이 계약이다.** 서버가 처음 기동할 때
// room 하나와 코드 두 벌을 발급하고, 저장소에 room 이 이미 있으면 아무것도 만들지
// 않는다. 앱에는 room 을 만드는 화면도 그 요청을 받는 엔드포인트도 없고 스키마가
// 두 번째 room 행을 거절한다. 문을 지키는 대신 문을 없앤 결정이라, 문이 다시 생기면
// 그 결정이 조용히 뒤집힌다 — tests/contract/snapshot.test.ts 가 그것을 지킨다.

import { Type } from '@sinclair/typebox'

import * as S from './schema.ts'

export const routes = {
  /** 시계 동기 — 네 타임스탬프 왕복. 열려 있다(입장 전에도 계측 프로브가 쓴다). */
  clockSync: {
    method: 'POST',
    path: '/api/clock/sync',
    auth: 'public',
    body: S.ClockSyncRequest,
    response: S.ClockSyncResponse,
  },

  /**
   * 고정줄이 쓰는 room 공개 정보 — **작전 타입까지다.**
   *
   * 응답 모양에 `ctNickname` 칸이 있지만 **증명되지 않은 요청에는 언제나 null 이 실린다.**
   * 값을 내는 자리는 아래 `joinCheck`(코드를 맞힌 사람)와 WebSocket 의 `room.state`
   * (이미 들어온 사람) 둘이다. 칸을 지우지 않고 비워 보내는 이유는 같은 `RoomPublic`
   * 모양을 그 둘이 함께 쓰기 때문이고, 값을 아무에게나 주지 않는 이유는 주소가 이미
   * 공개됐다고 가정하기로 한 판정 때문이다 — 우리 CT 의 닉네임은 남이 다른 서버에
   * 우리 것처럼 보이는 room 을 세울 때 쓰는 재료다.
   */
  roomPublic: {
    method: 'GET',
    path: '/api/room',
    auth: 'public',
    response: S.RoomPublic,
  },

  /**
   * 입장 **두 번째 단계** — 코드만 검증하고 그 room 의 CT 닉네임을 돌려준다.
   *
   * 이 표면이 따로 있는 이유는 순서 때문이다. 정본이 요구하는 것은 「코드가 맞으면 CT
   * 닉네임이 표시되고, **그다음에** 게임 ID 를 넣는다」이고, 판정 계약도 "게임 ID 입력
   * 전이다" 라고 못박는다. 코드와 게임 ID 를 한 번에 받는 `join` 만 있으면 두 단계가
   * 한 화면으로 붙어 버리고, 그러면 오입장을 잡는다는 이 단계의 존재 이유가 사라진다.
   */
  joinCheck: {
    method: 'POST',
    path: '/api/join/check',
    auth: 'public',
    body: Type.Object({ code: S.JoinCode }),
    response: Type.Object({ ctNickname: S.Nickname }),
  },

  /** 입장 **세 번째 단계** — 공용 입장 코드 + 게임 ID. whitelist 대조로 nickname 을 붙인다. */
  join: {
    method: 'POST',
    path: '/api/join',
    auth: 'public',
    body: Type.Object({ code: S.JoinCode, gameId: S.GameId }),
    response: Type.Object({ sessionToken: S.Id, nickname: S.Nickname }),
  },

  /** room 관리 코드로 CT 임을 증명한다. 이 브라우저에 남은 토큰이 곧 CT 자격이다. */
  ctClaim: {
    method: 'POST',
    path: '/api/ct/claim',
    auth: 'public',
    body: Type.Object({ adminCode: S.AdminCode }),
    // 닉네임이 아직 비었는지를 함께 알린다. 이 한 칸이 1회성 단계 안내를 띄울지 말지를
    // 정하고, 그래서 화면이 그것을 알아내려고 관리 코드를 다시 보내지 않아도 된다.
    response: Type.Object({ ctToken: S.Id, ctNickname: Type.Union([S.Nickname, Type.Null()]) }),
  },

  /**
   * CT 닉네임을 세운다 — 1회성 단계 안내의 첫 칸이다.
   *
   * `ctClaim` 에 닉네임 칸을 얹지 않고 자리를 가른 이유는 흐름이 그렇게 생기지 않기
   * 때문이다. CT 는 관리 코드만으로 화면에 **먼저 들어가고**, 안내가 겹친 뒤에야
   * 닉네임을 적는다. 한 라우트로 합치면 화면이 그 시점에 관리 코드를 다시 들고 있어야
   * 하고, 그 값을 필요보다 오래 브라우저에 남기게 된다.
   */
  ctNickname: {
    method: 'POST',
    path: '/api/ct/nickname',
    auth: 'ct',
    body: Type.Object({ ctNickname: S.Nickname }),
    response: Type.Object({ room: S.RoomPublic }),
  },

  /** 코드 패널의 **드러냄** — 살아 있는 CT 세션이 관리 코드를 되찾는 첫째 경로다. */
  ctCodes: {
    method: 'GET',
    path: '/api/ct/codes',
    auth: 'ct',
    response: S.RoomCodes,
  },

  /**
   * 코드 패널의 **재발급.** 대상을 인자로 받아 **한 번에 하나만** 간다.
   *
   * 둘을 함께 가는 문을 두지 않은 이유가 정본에 있다 — 관리 코드 재발급이 공용 입장
   * 코드를 건드리면 코드 하나를 되찾자고 20~50명 재배포를 강요하는 셈이 된다. 인자
   * 없이 "재발급" 한 동사만 두면 그 사고가 누르는 실수 한 번으로 일어난다.
   */
  ctCodesRotate: {
    method: 'POST',
    path: '/api/ct/codes/rotate',
    auth: 'ct',
    body: Type.Object({ target: S.RotateTarget }),
    response: S.RoomCodes,
  },

  /** CT 가 보는 whitelist 전량. */
  ctWhitelist: {
    method: 'GET',
    path: '/api/ct/whitelist',
    auth: 'ct',
    response: Type.Object({ entries: Type.Array(S.WhitelistEntry) }),
  },

  /**
   * whitelist 에 게임 ID 와 닉네임 쌍을 등록한다.
   *
   * **다른 alliance 소속을 이유로 거부하지 않는다.** 이 명단은 소속을 가리는 장치가
   * 아니라 CT 가 "이 사람에게 신호를 보낸다" 고 정한 목록이고, 연합 작전에서 남의
   * alliance 사람이 들어오는 것이 정상이기 때문이다.
   */
  ctWhitelistAdd: {
    method: 'POST',
    path: '/api/ct/whitelist',
    auth: 'ct',
    body: S.WhitelistDraft,
    response: Type.Object({ entries: Type.Array(S.WhitelistEntry) }),
  },

  /**
   * 적 roster 를 명부에 올린다 — **닉네임만으로 등록된다.**
   *
   * marching 을 필수로 걸 수 없는 것이 계약이다. 적의 행군 시간은 공수가 교대될 때 한
   * 번씩 알아내 누적하는 값이라 처음 관측되는 순간에는 이름밖에 없다. 그때 등록을 막으면
   * 그 적의 rally 를 아예 적어 둘 수 없고, 「착탄 미상(입력필요)」이라는 계약이 애초에
   * 성립하지 않는다.
   *
   * **아군은 이 문으로 오지 않는다.** 아군 명부는 whitelist 명단 그 자체이고, 사람을
   * 올리는 문이 둘이면 게임 ID 와 닉네임의 짝이 두 곳에 생긴다.
   */
  enemyRosterAdd: {
    method: 'POST',
    path: '/api/rosters/enemy',
    auth: 'ct',
    body: Type.Object({
      nickname: S.Nickname,
      marchingMs: Type.Optional(Type.Union([S.Millis, Type.Null()])),
    }),
    response: Type.Object({ rosters: Type.Array(S.Roster) }),
  },

  /**
   * 행군 시간을 고친다. **이것이 marching 을 고치는 유일한 문이다.**
   *
   * rally 편집기에도 열람 화면에도 이 문을 내지 않는다. 문이 여럿이면 dispatch 된
   * 시퀀스가 구성 시점 delta 로 확정된다는 계약이 조용히 무너진다 — 어느 문으로
   * 고쳤는지에 따라 진행 중 시퀀스가 움직이기도 하고 안 움직이기도 하게 된다.
   *
   * `side` 를 함께 받는 이유: 아군은 whitelist 행이고 적군은 roster 행이라 저장 자리가
   * 다르다. 서버가 두 테이블을 차례로 뒤지게 두면 같은 id 가 양쪽에 있을 때 어느 쪽을
   * 고칠지가 조회 순서에 달리게 된다.
   */
  rosterMarching: {
    method: 'POST',
    path: '/api/rosters/marching',
    auth: 'ct',
    body: Type.Object({
      side: S.RosterSide,
      rosterId: S.Id,
      marchingMs: Type.Union([S.Millis, Type.Null()]),
    }),
    response: Type.Object({ rosters: Type.Array(S.Roster) }),
  },

  /**
   * 명부 행의 이름을 고친다. **이름을 고치는 문은 이것 하나다.**
   *
   * 「명부 행 삭제 — whitelist 취소 · 세션 회수 · 적군 하드 삭제」 케이스가 이 표면을
   * 요구한다.
   *
   * 등록 폼이 이름 수정을 겸하면 안 되는 이유가 실물로 있다. 여기가 서기 전까지는 이미
   * 있는 게임 ID 로 다시 등록하는 것이 곧 이름 수정이었는데, 그러면 CT 가 사람을
   * **더한다고 믿는 순간에** 멀쩡한 연맹원의 이름이 사라진다 — 무엇이 일어나는지가
   * 누른 버튼이 아니라 입력값에 달려 있다. 그래서 더하는 문과 고치는 문을 가른다.
   *
   * `side` 를 함께 받는 이유는 행군 갱신과 같다 — 아군은 whitelist 행이고 적군은
   * roster 행이라 저장 자리가 다르다.
   *
   * **행군은 이 문이 건드리지 않는다.** 고치는 문은 `rosterMarching` 하나다.
   *
   * 겹치는 이름은 거절한다. 닉네임은 CT 가 rally 를 배정할 때 사람을 가리키는 유일한
   * 표시라, 겹치면 화면 위에서 두 사람이 구별되지 않는다. 유일성은 **진영 안에서** 본다 —
   * 공수가 교대되면 같은 사람이 지난 전투의 적 명부에 남아 있을 수 있고 적 명부는 유휴
   * 초기화에서 보존되므로, 진영을 통틀어 막으면 CT 가 아군을 올리려다 지난 전투의 적
   * 줄에 막히고 푸는 길이 그 적 줄을 지우는 것뿐이 된다.
   */
  rosterNickname: {
    method: 'POST',
    path: '/api/rosters/nickname',
    auth: 'ct',
    body: Type.Object({
      side: S.RosterSide,
      rosterId: S.Id,
      nickname: S.Nickname,
    }),
    response: Type.Object({ rosters: Type.Array(S.Roster) }),
  },

  /**
   * 명부 행을 지운다.
   *
   * 「명부 행 삭제 — whitelist 취소 · 세션 회수 · 적군 하드 삭제」 케이스가 이 표면을
   * 요구한다.
   *
   * **아군을 지우는 것은 whitelist 등재를 취소하는 것이고, 그 게임 ID 로 들어와 있던
   * 접속을 전부 끊는다.** 같은 ID 의 다중 접속을 허용하므로 끊기는 것은 세션 하나가
   * 아니라 그 ID 의 접속 전부다. 명단에서만 빼고 소켓을 살려 두면 그 화면은 계속 상태를
   * 받고, 지운 사람이 여전히 신호를 듣는다.
   *
   * **추방과 결과가 다르다.** 추방은 지금 붙어 있는 접속을 끊을 뿐이라 그 사람은 명단에
   * 그대로 있어 곧바로 다시 들어온다. 이 문은 명단에서 지우므로 같은 코드로도 다시
   * 들어오지 못한다.
   *
   * **적군은 하드 삭제이고 누적 marching 이 함께 사라진다.** 유휴 6시간 초기화가 적
   * 명부를 보존하는 것과 다른 층이다 — 앞은 시계가 내리는 판단이라 그 줄이 다음 전투에
   * 쓰일지 알 수 없고 모르면 남기는 쪽이 안전하다. 이 문은 사람이 그 줄을 눈으로 보고
   * 「다음 전투에도 안 쓴다」고 내리는 판단이다. 보존은 기계가 지우지 않는다는 뜻이지
   * 사람이 지울 수 없다는 뜻이 아니다. 다만 다시 못 모으는 값이므로 화면이 확인을 한 번
   * 거치고, 사라질 값을 먼저 글자로 말한다.
   *
   * 키가 게임 ID 가 아니라 행 id 인 이유는 이 버튼이 앉는 줄이 `Roster` 이고 그 타입에
   * 게임 ID 칸이 없기 때문이다. 서버가 그 행에서 게임 ID 를 읽어 세션을 끊으므로 화면이
   * 게임 ID 를 손에 들고 있을 이유가 없다.
   */
  rosterRemove: {
    method: 'POST',
    path: '/api/rosters/remove',
    auth: 'ct',
    body: Type.Object({
      side: S.RosterSide,
      rosterId: S.Id,
    }),
    response: Type.Object({ rosters: Type.Array(S.Roster) }),
  },

  /**
   * CT 추방 — 접속 명단에서 즉시 수동 해제한다.
   *
   * **세션 하나가 아니라 게임 ID 한 줄을 끊는다.** 명단이 ID 당 한 줄이라 CT 가 화면에서
   * 고를 수 있는 단위가 줄이고, 세션 단위로 받으면 화면에 없는 것을 인자로 요구하게
   * 된다. 추방은 입장 코드 재발급과 함께 쓰는 수동 대응이지 자동 층이 아니다.
   */
  ctKick: {
    method: 'POST',
    path: '/api/ct/kick',
    auth: 'ct',
    body: Type.Object({ gameId: S.GameId }),
    response: Type.Object({ entries: Type.Array(S.PresenceEntry) }),
  },

  presence: {
    method: 'GET',
    path: '/api/presence',
    auth: 'session',
    response: Type.Object({ entries: Type.Array(S.PresenceEntry) }),
  },

  rosters: {
    method: 'GET',
    path: '/api/rosters',
    auth: 'session',
    response: Type.Object({ rosters: Type.Array(S.Roster) }),
  },

  sequence: {
    method: 'GET',
    path: '/api/sequence',
    auth: 'session',
    response: S.Sequence,
  },

  // ── 시퀀스를 쓰는 문 여섯 ───────────────────────────────────────────────
  //
  // 여럿으로 가른 이유는 **CT 의 작업 순서가 단계로 나뉘고 각 단계가 서로 다른 것을
  // 막기 때문이다.** 한 문으로 묶으면 무엇을 막아야 하는지가 본문의 어느 칸이 왔는지에
  // 달리고, 그때 「지금 이 조작이 허용되는가」의 판정이 라우트가 아니라 분기에 흩어진다.
  //
  //   조립(rally 더하기·빼기) → sequence confirm → 손잡이 → dispatch → 접기
  //
  // 마지막 하나가 2026-08-13 에 붙었다. 앞의 다섯이 한 작전을 **만드는** 문이고 그것은
  // 한 방향이라, 내보낸 뒤 그 방에서 다음 작전을 시작할 길이 없었다.
  //
  // 전부 CT 전용이다. 열람하는 사람은 `GET /api/sequence` 하나만 읽는다. 응답이 전부
  // `Sequence` 한 벌인 것도 규칙이다 — 조작마다 다른 모양을 돌려주면 화면이 조작별로
  // 다른 병합 코드를 갖게 되고, 그 코드들이 서로 다르게 낡는다.

  /**
   * rally 한 줄을 조립에 더한다. **행군 시간은 본문에 없다.**
   *
   * 그 값은 명부가 소유하므로 서버가 `rosterId` 로 읽어 온다. 본문으로 받으면 화면이
   * 들고 있던 낡은 값이 그대로 저장될 수 있고, 그때 rally 는 명부에 없는 행군으로
   * 계산된다 — 명부를 고쳐도 안 고쳐지는 rally 가 생긴다.
   *
   * **행군이 비어 있는 사람은 거절한다.** 「입력필요」인 사람을 배정하면 개시 시각이
   * 계산되지 않는데, 조용히 0 으로 두면 그 rally 만 지금 당장 열리는 것으로 나온다.
   */
  sequenceRallyAdd: {
    method: 'POST',
    path: '/api/sequence/rallies',
    auth: 'ct',
    body: Type.Object({
      type: S.RallyType,
      rosterId: S.Id,
      rallyingMs: S.Millis,
      // main 은 착탄축의 원점이라 0 이어야 한다. 서버가 그것을 확인한다.
      offsetMs: S.Millis,
    }),
    response: S.Sequence,
  },

  /**
   * rally 한 줄을 뺀다. **main 보호가 서버에도 걸린다.**
   *
   * sub 가 하나라도 남아 있는 동안 main 은 지워지지 않는다 — main 이 없는 시퀀스는
   * 착탄축의 원점이 없어 성립하지 않는다. 화면에서 삭제 마크를 비활성으로 두는 것과
   * 서버가 못 지우게 하는 것은 다르고, 판정 계약이 이 항목에 부분 점수를 두지 않았다.
   */
  sequenceRallyRemove: {
    method: 'POST',
    path: '/api/sequence/rallies/remove',
    auth: 'ct',
    body: Type.Object({ rallyId: S.Id }),
    response: S.Sequence,
  },

  /**
   * 조립을 마치고 **시퀀스 편집 문맥으로 넘어간다.** 손잡이와 dispatch 가 여기서 생긴다.
   *
   * 이 전환이 라우트 하나로 서 있는 이유는 그것이 상태 전이이기 때문이다. 화면 전환으로만
   * 두면 브라우저를 새로 열었을 때 CT 가 어느 문맥에 있었는지 서버가 모른다.
   */
  sequenceConfirm: {
    method: 'POST',
    path: '/api/sequence/confirm',
    auth: 'ct',
    body: Type.Object({}),
    response: S.Sequence,
  },

  /**
   * 시간 손잡이를 놓는다. **하한 미만은 보정하지 않고 거절한다.**
   *
   * 사람이 직접 친 값이라 6초로 조용히 고쳐 주면 적은 값과 쓰인 값이 갈리고, 갈린 것을
   * 알려 줄 사람이 이 프로젝트에 없다. 반대로 sub 가 붙어 가장 이른 개시가 하한 안으로
   * 들어오는 것은 사람이 하지 않은 일이라 조용히 평행이동한다 — 그쪽은 이 문이 아니다.
   */
  sequenceHandle: {
    method: 'POST',
    path: '/api/sequence/handle',
    auth: 'ct',
    body: Type.Object({ handleMs: S.Millis }),
    response: S.Sequence,
  },

  /**
   * 내보낸다. **이 순간 절대 시각이 확정되고 앵커가 전 접속자에게 나간다.**
   *
   * 서버가 보내는 것은 앵커 하나뿐이고 카운트다운 틱은 보내지 않는다 — 각 브라우저가
   * 자기 동기 시계 위에서 센다. 틱을 보내면 화면의 숫자가 네트워크 지터를 그대로
   * 물려받아, 늦게 그려질 뿐이던 것이 **틀리게 그려지는 것**이 된다.
   *
   * 같은 순간에 각 rally 의 행군 시간이 그 행에 얼어붙는다. 그 뒤로 명부를 고쳐도 이
   * 시퀀스는 움직이지 않는다.
   */
  sequenceDispatch: {
    method: 'POST',
    path: '/api/sequence/dispatch',
    auth: 'ct',
    body: Type.Object({}),
    response: S.Sequence,
  },

  /**
   * **이 작전을 접는다** — 내보낸 시퀀스를 지우고 빈 조립 상태로 돌아간다.
   *
   * 이 문이 없으면 저장소가 그 방을 잠근다. `sequence` 는 room 당 한 행이 유니크 인덱스로
   * 강제되는데 `dispatched` 에서 `draft` 로 돌아가는 경로가 앞의 다섯에 없어서, **한 번
   * 내보내면 그 방에서 다음 작전을 짤 수 없었다.** 정본의 타이밍 모델 절이 「한 전투 동안
   * 시퀀스는 여러 번 만들어진다」고 적었으므로 그 상태는 계약 위반이다.
   *
   * **판정 계약의 37개 케이스 중 이 문을 요구하는 케이스는 없다.** 요구한 것은 위 정본
   * 한 줄과 2026-08-13 사용자 결정이고, 그 사실을 여기 적는 이유는 라우트마다 근거를
   * 남기는 규약 때문이다 — 근거를 안 적으면 다음 사람이 「어느 케이스가 이걸 쓰지」를
   * 찾다가 못 찾고, 못 찾은 문은 쓰이지 않는 문으로 오해되어 지워진다.
   *
   * **재구성 스텝이 쓸 문이 아니다.** retime 도 dispatch 후 수정도 counter rally 추가도
   * 전부 **같은 편성을 다시 쓰는** 길이라 편성을 보존한다. 이 문은 편성을 **버리는** 유일한
   * 길이고, 그래서 그쪽 자리를 미리 파 두지 않는다.
   *
   * **확인은 화면이 진다. 서버는 두 번 받지 않는다.** 명부 행 삭제와 같은 문법이다 —
   * 되돌릴 수 없다는 사실과 무엇이 사라지는지를 말하는 것이 화면의 일이고, 서버가 그것을
   * 대신하면 그 문구를 어디에도 못 적는다.
   *
   * **내보낸 시퀀스에서만 열린다.** 조립 중과 편집 중에는 rally 를 빼는 문이 이미 있고,
   * 그 상태에서 이 문까지 열면 「지우기」가 둘이 되어 어느 쪽이 무엇을 지우는지가 화면의
   * 설명에 달리게 된다.
   */
  sequenceDiscard: {
    method: 'POST',
    path: '/api/sequence/discard',
    auth: 'ct',
    body: Type.Object({}),
    response: S.Sequence,
  },

  // ── 내보낸 작전을 다시 쓰는 문 셋 ───────────────────────────────────────
  //
  // 위의 여섯이 한 작전을 **만드는** 문이라면 아래 셋은 이미 나간 작전을 **다시 쓰는**
  // 문이다. 접기가 편성을 버리는 유일한 길인 것과 정확히 반대쪽이라, 넷을 한 자리에
  // 모으지 않고 여기서 갈랐다.
  //
  // **셋째가 2026-08-17 에 붙었다.** 앞의 둘은 나간 작전을 **그 자리에서** 고치는 문이고,
  // 셋째는 그 작전을 **편집 문맥으로 되돌리는** 문이다. 앞의 둘로는 못 하는 일이 있어서
  // 생겼다 — 내보낸 뒤에는 줄을 뺄 수도 타입을 바꿀 수도 없고, 그 둘이 필요한 CT 에게
  // 열려 있던 유일한 길이 접기였다. 접기는 편성을 지우므로 20명 배정을 처음부터 다시 넣게
  // 된다. 저장 구조가 그 우회를 막고 있기도 하다 — 시퀀스는 room 당 한 행이 유니크
  // 인덱스로 강제되어 **살아 있는 작전과 새 초안이 동시에 못 산다.**
  //
  // 진행 중 시퀀스에 rally 를 한 줄 더 붙이는 counter rally 는 **새 문이 아니다** —
  // `sequenceRallyAdd` 가 그대로 받는다. 조립할 때와 붙일 때의 본문이 같은 넷이라 문을
  // 하나 더 열면 같은 것을 받는 문이 둘이 되고, 그 둘의 검사가 서로 다르게 낡는다.

  /**
   * **retime — 같은 편성으로 시간만 다시 잡는다.**
   *
   * 보존하는 것이 계약이다: roster 배정 · rally 타입 · `rallying` · `offset` 이 한 자리도
   * 바뀌지 않고 **시간 손잡이 하나만** 움직인다. 정본이 이것을 중요 기능으로 못박은 이유는
   * 게임의 성질에 있다 — 작전은 공수가 교대되며 반복되고, rally 하나가 실패해도 편성은
   * 그대로 쓸 수 있다. 그때 CT 가 roster 배정부터 다시 짜야 한다면 그것만으로 다음 기회를
   * 놓친다.
   *
   * **경로 둘이 이 문 하나를 쓴다.** 30초 고정 원클릭과 직접 설정이고, 갈리는 것은 본문의
   * `handleMs` 값뿐이다. 문을 둘로 가르면 하한 판정이 두 곳에 생기고 그 둘이 서로 다르게
   * 낡는다 — 「3초 예산은 30초 경로에만 건다」는 측정의 규약이지 문의 규약이 아니다.
   *
   * **행군은 여기서 명부의 지금 값으로 다시 언다.** 얼핏 「진행 중 시퀀스는 marching 변화와
   * 무관하다」와 어긋나 보이지만 정본이 같은 절에서 그 예외를 스스로 적었다 — "바뀐
   * marching 은 다음 구성과 retime 이 쓴다". 무개입이 말하는 것은 **명부를 고치는 조작이
   * 진행 중 시퀀스를 건드리지 않는다**는 것이고, retime 은 명부를 고치는 조작이 아니라 CT 가
   * 시퀀스를 다시 내보내는 조작이다. 그 사람이 명부에서 내려갔으면 얼어붙은 값을 그대로 둔다.
   */
  sequenceRetime: {
    method: 'POST',
    path: '/api/sequence/retime',
    auth: 'ct',
    body: Type.Object({ handleMs: S.Millis }),
    response: S.Sequence,
  },

  /**
   * **dispatch 후 수정 — 내보낸 작전의 한 줄을 고친다.**
   *
   * 정본이 "dispatch 이후에도 수정할 수 있다 — offset · `rallying` · 담당 roster 같은 rally
   * 필드의 편집을 잠그지 않는다" 고 적었다. 그래서 본문이 그 셋이다.
   *
   * **타입은 본문에 없다.** main 은 착탄축의 원점이라 진행 중 시퀀스에서 그것을 sub 로
   * 바꾸면 나머지 줄의 `offset` 이 전부 무엇 대비인지 모르는 값이 된다. 정본의 편집 허용
   * 목록에도 타입이 없다 — 받지 않는 것이 곧 그 계약이고, 받아 놓고 거절하면 화면이 고칠 수
   * 있는 것처럼 보이는 칸을 하나 그리게 된다.
   *
   * **행군도 본문에 없다.** 고치는 문은 명부의 `rosterMarching` 하나뿐이라는 것이 정본의
   * 계약이고, 여기에 둘째 문을 열면 어느 값이 정본인지 알 수 없게 된다. 담당 roster 를
   * 바꾸면 그 사람의 지금 행군이 그 줄에 새로 얼어붙는다 — 그 줄에는 얼릴 값이 애초에 없다.
   * **같은 roster 로 두고 다른 칸만 고치면 얼어붙은 값은 그대로다.**
   *
   * **위반은 표시하지 않고 만들지 못하게 막는다.** 판정식은 이 제품에 하나뿐이다 —
   * `open ≥ now + 6초`. 경계값은 통과한다. `발사가능 ✗` 같은 위반 상태 표시는 폐지됐고,
   * 그 결과 열람 화면에는 유효한 시퀀스만 도착한다.
   */
  sequenceRallyEdit: {
    method: 'POST',
    path: '/api/sequence/rallies/edit',
    auth: 'ct',
    body: Type.Object({
      rallyId: S.Id,
      rosterId: S.Id,
      rallyingMs: S.Millis,
      offsetMs: S.Millis,
    }),
    response: S.Sequence,
  },

  /**
   * **이 작전 수정 — 내보낸 작전을 편성 그대로 편집 문맥으로 되돌린다.**
   *
   * 상태가 `dispatched → confirmed` 로 내려가고 착탄 원점이 `null` 이 된다. **rally 는 한
   * 줄도 건드리지 않는다** — 손잡이 값도 그대로 남는다(dispatch 가 그것을 지우지 않으므로
   * 아무것도 안 하는 것이 곧 보존이다). 되돌린 상태에서 CT 는 줄을 빼고 더하고 타입을
   * 바꾸고 손잡이를 다시 놓은 뒤 다시 내보낸다.
   *
   * **왜 `draft` 가 아니라 `confirmed` 인가.** `confirmed` 에서 줄 빼기·줄 더하기·손잡이가
   * 이미 전부 열려 있어 **편집 규칙을 한 줄도 새로 안 쓴다.** `draft` 로 내리면 사람이 이미
   * 누른 confirm 까지 무르는 일이 되어, 되돌리기가 편성이 아니라 작업을 되돌린다.
   *
   * **왜 「줄 빼기만 여는 것」이 아닌가.** 그쪽은 「이미 출발한 사람의 줄을 지운다」는,
   * 일부러 막아 둔 규칙을 푸는 일이다. 되돌리기는 규칙을 풀지 않고 문맥을 옮긴다.
   *
   * **치르는 값이 있고 그것을 화면이 말한다.** 원점이 사라지므로 **연맹원의 카운트다운이
   * 사라진다** — 이미 개시한 사람은 병력이 날아가는 중인데 화면이 빈다. 그리고 얼어붙었던
   * 행군이 녹아 명부의 지금 값으로 돌아간다(`readSequence` 가 상태로 출처를 가른다). 둘 다
   * retime 이 이미 쓰는 규칙과 같지만, retime 과 달리 이쪽은 **작전이 잠시 없어진다.**
   *
   * **확인은 화면이 진다. 서버는 두 번 받지 않는다.** 접기·명부 행 삭제와 같은 문법이다 —
   * 무엇이 사라지는지 말하는 것이 화면의 일이고, 서버가 그것을 대신하면 그 문구를 어디에도
   * 못 적는다.
   *
   * **내보낸 시퀀스에서만 열린다.** 조립 중과 편집 중에는 되돌릴 것이 없다.
   */
  sequenceReopen: {
    method: 'POST',
    path: '/api/sequence/reopen',
    auth: 'ct',
    body: Type.Object({}),
    response: S.Sequence,
  },

  enemyRallies: {
    method: 'GET',
    path: '/api/defense/enemy-rallies',
    auth: 'session',
    response: Type.Object({ enemyRallies: Type.Array(S.EnemyRally) }),
  },

  // ── 적 rally 를 쓰는 문 셋 ──────────────────────────────────────────────
  //
  // **수비에는 dispatch 게이트가 없다.** 등록도 재앵커도 삭제도 전부 즉시 전파다. 공격은
  // 조립을 마치고 시각을 확정하는 단계가 있지만, 수비는 적이 이미 날아오는 중이라 확정할
  // 것이 없다 — CT 가 본 것을 넣는 즉시 그것이 지금의 사실이다.
  //
  // 셋 다 응답이 목록 한 벌인 것도 규칙이다. 조작마다 다른 모양을 돌려주면 화면이 조작별로
  // 다른 병합 코드를 갖게 되고, 그 코드들이 서로 다르게 낡는다.

  /**
   * 적 rally 한 줄을 올린다. **앵커 시각은 본문에 없다 — 서버가 찍는다.**
   *
   * 클라이언트 시각을 앵커로 쓰면 실데이터 규칙 위반이고, 여섯 나라에서 접속하는 이
   * 제품에서는 그 오차가 그대로 착탄 오차가 된다. CT 가 시계를 읽어 적을 필요가 없다는
   * 것이 이 화면의 핵심 편의이기도 하다.
   *
   * **단계를 받는다.** 같은 `0:52` 가 rallying 중이면 아직 출발하지 않았다는 뜻이고 행군
   * 중이면 이미 날아오고 있다는 뜻이라, 단계를 안 받으면 착탄이 적 marching 하나만큼
   * 통째로 어긋난다.
   *
   * **타입도 offset 도 없다.** CT 는 상대의 main/sub 를 알 수 없고, 순서는 계산된 착탄만이
   * 정한다. 받지 않는 것이 곧 그 계약이다.
   *
   * **roster 는 명부에 있는 것만 온다.** 폼에서 새로 만드는 문은 없다 — 만드는 자리가
   * 둘이면 같은 사람이 두 줄로 생기고, 삭제 라우트가 지우는 행과 화면이 읽는 행이 갈린다.
   */
  enemyRallyAdd: {
    method: 'POST',
    path: '/api/defense/enemy-rallies',
    auth: 'ct',
    body: Type.Object({
      enemyRosterId: S.Id,
      phase: S.EnemyPhase,
      remainingMs: S.Millis,
    }),
    response: Type.Object({ enemyRallies: Type.Array(S.EnemyRally) }),
  },

  /**
   * 그 줄을 **다시 앵커한다.** 값 갱신이 아니라 새 기준을 찍는 일이다.
   *
   * 사람이 인게임 카운트다운을 **다시 본** 순간이 새 기준이므로, 서버가 그 순간을 새로
   * 찍는다. 잘못 읽은 값의 대응 수단일 뿐 아니라 **정확도를 올리는 수단**이기도 하다 —
   * rallying 으로 앵커해 둔 행을 행군 중에 다시 앵커하면 적 marching 항이 식에서 빠져
   * 명부 오차가 그 행에서 사라진다.
   *
   * 그래서 **단계를 바꿔 앵커할 수 있어야 한다.** 그것이 이 문의 존재 이유의 절반이다.
   */
  enemyRallyReanchor: {
    method: 'POST',
    path: '/api/defense/enemy-rallies/reanchor',
    auth: 'ct',
    body: Type.Object({
      enemyRallyId: S.Id,
      phase: S.EnemyPhase,
      remainingMs: S.Millis,
    }),
    response: Type.Object({ enemyRallies: Type.Array(S.EnemyRally) }),
  },

  /**
   * 그 줄을 지운다. 잘못 본 rally 나 이미 끝난 rally 를 목록에서 내린다.
   *
   * **확인은 화면이 진다. 서버는 두 번 받지 않는다.** 명부 행 삭제·이 작전 접기와 같은
   * 문법이다 — 무엇이 사라지는지를 말하는 것이 화면의 일이고, 서버가 그것을 대신하면 그
   * 문구를 어디에도 못 적는다.
   */
  enemyRallyRemove: {
    method: 'POST',
    path: '/api/defense/enemy-rallies/remove',
    auth: 'ct',
    body: Type.Object({ enemyRallyId: S.Id }),
    response: Type.Object({ enemyRallies: Type.Array(S.EnemyRally) }),
  },

  /**
   * **연맹원이 열람 화면을 처음 열었을 때 먼저 보일 탭을 정한다** (2026-08-15 사용자 결정).
   *
   * 2026-08-03 에 이 값은 room 의 **배타적 작전 타입**이었고 그것을 바꾸는 문이 「수동
   * 전환」이었다. 그 모델은 「전환 순간을 어떻게 표현할 것인가」라는 미결을 낳았고, 더
   * 나쁘게는 **CT 가 수비를 짜려고 전환하는 순간 공격 카운트다운을 보던 roster 가 자기
   * 차례를 잃는** 자리를 만들었다 — 이 제품이 없애려는 실패를 제품이 직접 만드는 자리다.
   *
   * 지금은 attack 과 defense 가 **나란히 선 보기 둘**이라 전환이라는 사건이 없다. 그래서
   * 이 문이 정하는 것은 상태가 아니라 **기본값 하나**다. 값이 여전히 필요한 이유는 전투
   * 중에 「지금은 수비다」를 20~50명이 각자 알아내게 하면 그것이 곧 지연이기 때문이다.
   *
   * **조작을 하나 더 만들지 않는다.** CT 가 작전 구성 화면에서 attack 탭과 defense 탭 중
   * 무엇을 보고 있는지가 이 값을 정하므로, 화면이 탭을 옮길 때 이 문을 두드린다.
   */
  roomDefaultView: {
    method: 'POST',
    path: '/api/room/default-view',
    auth: 'ct',
    body: Type.Object({ operation: S.OperationType }),
    response: S.RoomPublic,
  },
} as const

export type RouteName = keyof typeof routes
export const routeNames = Object.keys(routes) as RouteName[]

/**
 * 어떤 경로도 room 생성 표면이 되어서는 안 된다. 스냅샷 테스트가 이 목록을 근거로
 * 검사하고, 새 라우트를 더하다 실수로 문을 만들면 커밋 전에 걸린다.
 */
export const FORBIDDEN_PATH_FRAGMENTS = ['/rooms', '/room/create', '/room/new'] as const
